import type { Passage } from "./types";

export const passage: Passage = {
  slug: "matthew-11-28-30",
  reference: "Matthew 11:28–30",
  title: "Come to me all who are weary: the rest with a yoke on it",
  h1: "Matthew 11:28–30, and the yoke nobody quotes",
  description:
    "I will give you rest is the famous half. The next sentence hands you a yoke, which changes what kind of rest is being offered.",
  targets: [
    "matthew 11 28 meaning",
    "come to me all who are weary explained",
    "my yoke is easy and my burden is light meaning",
  ],
  published: "2026-09-03",
  standfirst:
    "The invitation is quoted everywhere and it is two sentences long. The second sentence is the one that says what the rest consists of.",
  verse: {
    text: "Come to me, all you who labor and are heavily burdened, and I will give you rest. Take my yoke upon you and learn from me, for I am gentle and humble in heart; and you will find rest for your souls. For my yoke is easy, and my burden is light.",
    ref: "Matthew 11:28–30",
  },
  context: [
    {
      text: 'At that time, Jesus answered, "I thank you, Father, Lord of heaven and earth, that you hid these things from the wise and understanding, and revealed them to infants. Yes, Father, for so it was well-pleasing in your sight."',
      ref: "Matthew 11:25–27",
    },
    {
      text: "For everything there is a season, and a time for every purpose under heaven:",
      ref: "Ecclesiastes 3:1",
    },
  ],
  takeaways: [
    "The rest is offered twice and the second time it arrives after taking a yoke — it is the rest of pulling with someone, not the rest of stopping.",
    "A yoke in this setting is a teacher's whole way of reading and living. Take my yoke means learn from me, which the sentence says explicitly.",
    "Easy translates a word closer to well-fitting or kindly. The claim is about the fit of the burden rather than its absence.",
    "The people being addressed are already carrying something. Nothing here is aimed at the unoccupied.",
  ],
  body: [
    {
      type: "h2",
      id: "the-second-sentence",
      text: "The second sentence",
    },
    {
      type: "p",
      text: "Almost every reproduction of this passage stops at rest. What follows immediately is take my yoke upon you and learn from me — an instruction to put something on rather than to set something down.",
    },
    {
      type: "p",
      text: "The passage then offers rest a second time, and this one is qualified: rest for your souls, found after the yoke has been taken. Two rests, in a specific order, and the second is the one being explained.",
    },
    {
      type: "h2",
      id: "what-a-yoke-is",
      text: "What a yoke meant",
    },
    {
      type: "p",
      text: "Literally, the beam across two animals that lets them pull together. In first-century Jewish usage it was also a standard image for a teacher's or a tradition's whole discipline — the yoke of the law, the yoke of a rabbi's interpretation.",
    },
    {
      type: "p",
      text: "So take my yoke is close to become my student. The sentence says as much in its own next clause: and learn from me. What is on offer is not the removal of obligation but a different one, and a teacher described as gentle and humble in heart rather than exacting.",
    },
    {
      type: "h2",
      id: "the-word-easy",
      text: 'The word "easy"',
    },
    {
      type: "p",
      text: "The Greek adjective is chrestos, used elsewhere for kindness and for something that is good or serviceable. Applied to a yoke it suggests one that has been fitted properly — the difference between a harness that chafes and one that does not.",
    },
    {
      type: "p",
      text: "Which is a claim about how the burden sits rather than about how much it weighs. Read that way the sentence is consistent with the rest of the gospel, where following costs a great deal and is not described as effortless anywhere.",
    },
  ],
  misreading: {
    claim:
      "That it is an offer of rest in the ordinary sense — stop, put it down, be at peace — and therefore a verse for exhaustion and burnout, promising relief from the load.",
    correction: [
      "The first half genuinely says that, which is why the reading is so natural, and it is not wrong so much as truncated. Somebody exhausted is exactly who is being addressed; the passage names them as labouring and heavily burdened.",
      "But the rest described in the second sentence is the rest of a well-fitted yoke pulled alongside somebody gentle, not the rest of an empty afternoon. That distinction matters most to the person for whom stopping is not available — the carer, the parent, the person whose situation is not going to change this year. A verse that only works if you can put the thing down is no use to them.",
      "Read whole, it offers something they can actually have, which is why the correction is worth making rather than pedantic: not an exit from the load, but a different way of carrying it, a fitting that does not chafe, and different company while doing so.",
      "It is also worth being precise about what the passage is contrasting itself with. The burden being lifted is not work or responsibility; the paragraph before it is about religious teachers and what they had made of the law, and Matthew returns to the theme later with a description of loads bound on other people's shoulders. The yoke on offer is set against those, not against ordinary life.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read all three verses, out loud if possible. The passage is one sentence longer than the version most people carry, and the extra sentence is the one that makes it usable in a week that cannot be stopped.",
    },
    {
      type: "p",
      text: "Pair it with an actual stopping practice rather than substituting for one. Verse 28 does not remove the case for {a day off}; it addresses the six days that are not.",
      links: { "a day off": "/blog/digital-sabbath" },
    },
    {
      type: "note",
      text: "The verses immediately before it explain the tone. Jesus has just thanked the Father for hiding these things from the wise and understanding and revealing them to infants — a sentence that sounds harsh until you notice who is standing there. The invitation that follows is addressed to precisely the people the wise had written off, and gentle and humble in heart is the contrast being drawn with the teachers they had been failing to satisfy.",
    },
    {
      type: "note",
      text: "If the exhaustion is physical rather than spiritual, the passage is still true and sleep is still the answer. The two are not competing.",
    },
  ],
  faq: [
    {
      q: "What does Matthew 11:28 mean?",
      a: "It invites people already carrying something to come and be given rest — and the next verse defines that rest as what is found after taking a yoke and learning from a gentle teacher, rather than as putting the load down.",
    },
    {
      q: "What does my yoke is easy mean?",
      a: "The Greek word suggests well-fitting or kindly rather than light. It is a claim about how the burden sits, not about its weight — the difference between a harness that chafes and one fitted properly.",
    },
    {
      q: "Is Matthew 11:28 about rest from work?",
      a: "Not primarily. The rest offered arrives with a yoke attached and is described as rest for your souls. It is the rest of pulling alongside somebody rather than of stopping, which is what makes it usable when stopping is not available.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all three verses are printed rather than the first one alone. It reads 'gentle and humble in heart' where the King James has 'meek and lowly'; the yoke in verse 29 is there in every translation.",
    },
  ],
  related: ["digital-sabbath", "morning-quiet-time-routine"],
  relatedTopics: ["bible-verses-about-rest", "bible-verses-about-peace"],
  relatedPassages: ["psalm-23", "isaiah-41-10"],
};
